import "./instrument.ts";

import { Trans, useLingui } from "@lingui/react/macro";
import {
  Alert,
  Code,
  Flex,
  Loader,
  MantineProvider,
  Text,
} from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import * as Sentry from "@sentry/react";
import { UrqlProvider } from "@sweco-se/reality-frontend";
import "@sweco-se/reality-frontend/style.css";
import { cssVariablesResolver, theme } from "@sweco/theme-mantine";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { WebStorageStateStore } from "oidc-client-ts";
import { register } from "ol/proj/proj4";
import proj4 from "proj4";
import { StrictMode, useEffect } from "react";
import type { FC } from "react";
import ReactDOM from "react-dom/client";
import { AuthProvider, useAuth } from "react-oidc-context";
import type { AuthProviderProps } from "react-oidc-context";

import { api, queryClient } from "@/api.ts";
import { useAutoSignin } from "@/hooks/useAutoSignin.ts";
import LocaleProvider from "@/locales";
import { router } from "@/router.ts";

import reportWebVitals from "./reportWebVitals.ts";
import "./styles.css";

proj4.defs(
  "EPSG:3006",
  "+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs",
);
register(proj4);

const oidcConfig = {
  authority:
    (window as any).REALITY_OIDC_ISSUER ??
    import.meta.env.VITE_OIDC_ISSUER ??
    "http://localhost:5110",
  client_id:
    (window as any).REALITY_OIDC_CLIENT_ID ??
    import.meta.env.VITE_OIDC_CLIENT_ID ??
    "12345678",
  scope:
    `openid profile email ${(window as any).REALITY_OIDC_SCOPES ?? import.meta.env.VITE_OIDC_SCOPES ?? ""}`.trim(),
  redirect_uri: `${window.location.origin}${import.meta.env.BASE_URL || "/"}`,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  onSigninCallback: (user) => {
    window.history.replaceState(
      {},
      "",
      typeof user?.state === "string" && user.state.startsWith("/")
        ? user.state
        : window.location.pathname,
    );
  },
} satisfies AuthProviderProps;

const App: FC = () => {
  const auth = useAuth();

  useEffect(() => {
    if (auth.user) {
      Sentry.setUser({
        id: auth.user.profile.sub,
        email: auth.user.profile.email,
        username: auth.user.profile.preferred_username,
      });
      api.headers.set("Authorization", `Bearer ${auth.user.access_token}`);
      queryClient.invalidateQueries().then(() => {});
    } else {
      Sentry.setUser({});
    }
  }, [auth.user]);

  const { isLoading, isAuthenticated, error } = useAutoSignin({
    signinMethod: "signinRedirect",
    args: {
      state: window.location.pathname + window.location.search,
    },
  });

  const { t } = useLingui();

  if (isLoading) {
    return (
      <Flex w="100vw" h="100vh" justify="center" align="center">
        <Loader size="xl" color="green" />
      </Flex>
    );
  } else if (error) {
    return (
      <Flex w="100vw" h="100vh" justify="center" align="center">
        <Alert variant="light" color="alert" title={t`Something went wrong`}>
          <Text my="lg" size="sm">
            <Trans>
              Additional information, please include this if you want to report
              an error:
            </Trans>
          </Text>
          <Code block p="sm" bg="white">
            {error.message}
          </Code>
        </Alert>
      </Flex>
    );
  } else if (isAuthenticated) {
    return <RouterProvider router={router} context={{ queryClient, auth }} />;
  } else {
    return null;
  }
};

export const GRAPHQL_ENDPOINT =
  import.meta.env.VITE_GRAPHQL_ENDPOINT ?? "/graphql";

const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <StrictMode>
      <LocaleProvider>
        <AuthProvider {...oidcConfig}>
          <UrqlProvider endpoint={GRAPHQL_ENDPOINT}>
            <QueryClientProvider client={queryClient}>
              <MantineProvider
                theme={theme}
                cssVariablesResolver={cssVariablesResolver}
              >
                <ModalsProvider>
                  <Notifications autoClose={10_000} />
                  <App />
                </ModalsProvider>
              </MantineProvider>
            </QueryClientProvider>
          </UrqlProvider>
        </AuthProvider>
      </LocaleProvider>
    </StrictMode>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
